import { BackendUnreachable, InvalidFetchAthenaEventsFilters, } from "../EventStorageClient/errors.js";
import { describeThrownValueDetails, extractThrownValueDetails, } from "../utils/extractThrownValueDetails.js";
function normalizePullCause(cause) {
    if (cause instanceof BackendUnreachable) {
        return {
            kind: "backendUnreachable",
            details: extractThrownValueDetails(cause.cause),
        };
    }
    if (cause instanceof InvalidFetchAthenaEventsFilters) {
        return {
            kind: "invalidFilters",
            filters: cause.filters,
            responseBody: cause.responseBody,
        };
    }
    return { kind: "thrown", details: extractThrownValueDetails(cause) };
}
function describeReplicationPullCause(cause) {
    switch (cause.kind) {
        case "backendUnreachable":
            return `backend unreachable (${describeThrownValueDetails(cause.details)})`;
        case "invalidFilters":
            return "invalid filters supplied to fetchAthenaEvents";
        case "thrown":
            return describeThrownValueDetails(cause.details);
    }
}
export class AthenaEventStreamNotStarted extends Error {
    constructor() {
        super("AthenaEventStream has not yet started. Start it by calling AthenaEventStream.start()");
        this.name = "AthenaEventStreamNotStarted";
    }
}
export class ReplicationPushWrongBatchSizeError extends Error {
    batchSize;
    constructor(batchSize) {
        super(`Push batch size should always be 1, got ${batchSize}`);
        this.batchSize = batchSize;
        this.name = "ReplicationPushWrongBatchSizeError";
    }
}
export class ReplicationPushRetryableError extends Error {
    athenaEventSaveResult;
    constructor(athenaEventSaveResult) {
        super(`Replication push failed with retryable error ${athenaEventSaveResult.error.reason}: ${describeThrownValueDetails(athenaEventSaveResult.error.details)}`);
        this.athenaEventSaveResult = athenaEventSaveResult;
        this.name = "ReplicationPushRetryableError";
    }
}
export class ReplicationPushNonRetryableError extends Error {
    athenaEventSaveResult;
    constructor(athenaEventSaveResult) {
        super(`Replication push failed with non-retryable error ${athenaEventSaveResult.error.reason}: ${describeThrownValueDetails(athenaEventSaveResult.error.details)}`);
        this.athenaEventSaveResult = athenaEventSaveResult;
        this.name = "ReplicationPushNonRetryableError";
    }
}
export class ReplicationPullError extends Error {
    cause;
    /** `cause`, normalized into a typed shape at creation time (see `normalizePullCause`). */
    normalizedCause;
    constructor(cause) {
        const normalizedCause = normalizePullCause(cause);
        super(`Replication pull failed with error: ${describeReplicationPullCause(normalizedCause)}`);
        this.cause = cause;
        this.name = "ReplicationPullError";
        this.normalizedCause = normalizedCause;
    }
}
export class LastSyncMarkerWriteError extends Error {
    cause;
    context;
    constructor(cause, 
    /**
     * Diagnostic context to tell apart a same-tab race (a previous
     * AthenaEventStream instance still tearing down while a new one starts),
     * a cross-tab race (another open tab holding a live instance for the
     * same user), and a cold local-doc cache (see `revisionBeforeUpsert`).
     */
    context) {
        super(`Failed to update the lastSyncInfo local document after replication settled: ${describeThrownValueDetails(extractThrownValueDetails(cause))}`);
        this.cause = cause;
        this.context = context;
        this.name = "LastSyncMarkerWriteError";
    }
}
export class SubscriberAlreadyRegistered extends Error {
    subscriberId;
    constructor(subscriberId) {
        super(`A subscriber with id ${subscriberId} is already registered`);
        this.subscriberId = subscriberId;
        this.name = "SubscriberAlreadyRegistered";
    }
}
export class SubscriberNotRegistered extends Error {
    subscriberId;
    constructor(subscriberId) {
        super(`No subscriber registered with id ${subscriberId}`);
        this.subscriberId = subscriberId;
        this.name = "SubscriberNotRegistered";
    }
}
export class SubscriberProcessAthenaEventError extends Error {
    error;
    athenaEvent;
    subscriberId;
    constructor(
    /** Error that occurred. */
    error, 
    /** `AthenaEvent` that was being processed when the error occurred. */
    athenaEvent, 
    /** Id of the subscriber that was processing the `AthenaEvent`. */
    subscriberId) {
        super(`Subscriber ${subscriberId} failed processing AthenaEvent ${athenaEvent.id}`);
        this.error = error;
        this.athenaEvent = athenaEvent;
        this.subscriberId = subscriberId;
        this.name = "SubscriberProcessAthenaEventError";
    }
}
//# sourceMappingURL=errors.js.map